<template>
    <div ref = "scrollContainer" id = "intro" direction = "vertical" class = "relative w-screen h-screen flex justify-center items-center z-10 pointer-events-none">
        <CrossfireCircles v-if="mountLazy" key="intro-circles" :site="site" ref = "circles" :timings="timings" :mainTimeline="mainTimeline" />
        <div key="intro-white-overlay" class = "top-0 left-0 absolute w-screen h-screen bg-[#000] disabled-at-end z-10 translate-z-0" ref = "whiteOverlay"></div>
        <div key="intro-logo-holder" ref = "logoHolder" class = "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 absolute w-screen h-screen object-contain min-h-[100dvh] flex justify-center items-center overflow-hidden will-change-[width,height] backface-hidden translate-z-0 disabled-at-end z-20">
            <video 
                ref="introVideo" 
                v-if="!videoFinished"
                class="z-20 absolute w-full h-full object-cover bg-black" 
                :src="videoSrc"
                muted
                playsinline
                preload="auto"
                @ended="onVideoEnded"
                @timeupdate="onVideoTimeUpdate"
                @error="onVideoError"
            ></video>
            <LottieAnimation 
                v-if="logoRendered && logoLottieData" 
                id="logoLottie" 
                :class="[
                    'z-10 absolute w-[min(100vw,100vh)] h-[min(100vw,100vh)] transition-opacity duration-100 top-0 left-0',
                    videoFinished ? 'opacity-100' : 'opacity-0 pointer-events-none'
                ]" 
                :path="logoLottieData" 
                @completed="triggerAnimationStage2" 
                trigger="function" 
                ref="logo"
            />
        </div>
        <div class="absolute bottom-[10vh] left-0 w-full h-[15vh] flex justify-center items-center z-30 pointer-events-none" v-show="animationStage2Finished">
            <div key="intro-scroll-holder" class="relative h-full w-[15vh] pointer-events-auto" ref="scrollHolder">
                <LottieAnimation v-if="scrollLottieData" class="absolute scale-[1] h-full top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2" :loop="true" :path="scrollLottieData" trigger="function" ref="scroll"/>
                <div class="text-center text-white text-md uppercase absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 whitespace-nowrap leading-[0px] -mt-[0.1vh]" ref="scrollText">{{ site.scrollToEnterText || 'Scroll to Enter' }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineAsyncComponent, markRaw } from 'vue';
import { gsap } from "gsap";
import LottieAnimation from '@/components/pieces/LottieAnimation.vue';
import logoLottieData from '@/assets/lottie/LOGO-Mask-Animation/Logo-Intro-Animation-Lottie.json';
import scrollLottieData from '@/assets/lottie/Scroll/Scroll-Indicator.json';
import { resolveAsset } from '@/components/helpers/asset';
const CrossfireCircles = defineAsyncComponent(() => import('@/components/pages/Circles.vue'));

export default {
    name: 'CrossfireIntro',
    components: { 
        LottieAnimation, 
        CrossfireCircles 
    },
    props: { timings: Object, mainTimeline: Object, site: Object, isReady: Boolean, mountLazy: Boolean },
    emits: ['unlockPage', 'videoEnded', 'preMount'],
    data() {
        return {
            inited: false,
            tl: null,
            resizeHandler: null,
            videoFinished: false,
            animationStage2Finished: false,
            triggered: false,
            logoLottieData: logoLottieData,
            scrollLottieData: scrollLottieData,
            videoEndedTriggered: false,
            logoRendered: true,
            videoSrc: resolveAsset('/assets-site/videos/Logo/intro.mp4'),
        };
    },
    mounted() {
        window.addEventListener('wheel', this.preventInteraction, { passive: false });
        window.addEventListener('touchmove', this.preventInteraction, { passive: false });
        window.addEventListener('keydown', this.preventKeydown, { passive: false });
    },
    beforeUnmount() {
        this.enableInteraction();
        if (this.videoSafetyTimeout) {
            clearTimeout(this.videoSafetyTimeout);
        }
        if (this.scrollCleanHandler) {
            window.removeEventListener('scroll', this.scrollCleanHandler);
        }
        if (this.fadeInTween) {
            this.fadeInTween.kill();
        }
    },
    methods: {
        resolveAsset,
        onVideoError(e) {
            console.error("Intro video loading error:", e);
            this.onVideoEnded();
        },
        preventInteraction(e) {
            e.preventDefault();
        },
        preventKeydown(e) {
            const keys = [32, 33, 34, 35, 36, 37, 38, 39, 40];
            if (keys.includes(e.keyCode)) {
                e.preventDefault();
            }
        },
        enableInteraction() {
            window.removeEventListener('wheel', this.preventInteraction);
            window.removeEventListener('touchmove', this.preventInteraction);
            window.removeEventListener('keydown', this.preventKeydown);
        },
        start() {
            this.logoRendered = true;
            
            // Safety timeout: if the video is stuck in loading/paused/stalled state 
            // after 3.5s, force skip video playback so the site is never stuck.
            this.videoSafetyTimeout = setTimeout(() => {
                if (!this.videoFinished && !this.videoEndedTriggered) {
                    console.warn("Intro video safety timeout: skipping video playback.");
                    this.onVideoEnded();
                }
            }, 3500);

            // Start the intro video playback immediately to maximize buffering and trigger LCP instantly
            const video = this.$refs.introVideo;
            if (video && video.src && !video.src.endsWith('/') && !video.src.includes('undefined') && !video.src.includes('null')) {
                video.play().catch(e => {
                    if (e.name === 'AbortError') {
                        console.log("Video play request was aborted gracefully.");
                        return;
                    }
                    console.error("Video play failed, skipping to lottie", e);
                    this.onVideoEnded();
                });
            } else {
                console.warn("No valid intro video source, skipping directly to lottie.");
                this.onVideoEnded();
            }
        },
        onVideoTimeUpdate() {
            const video = this.$refs.introVideo;
            if (!video) return;

            // 🚀 ELEGANT FLASH PREVENTION: Only render the Lottie logo behind the video 
            // after the video has safely rendered its first frames (currentTime > 0.1s)!
            if (!video.paused && video.currentTime > 0.1 && !this.logoRendered) {
                this.logoRendered = true;
            }

            // Safari ended event fallback: trigger early (500ms before end) to completely avoid Safari's last-frame freezing bug
            if (video.duration && (video.duration - video.currentTime < 0.5 || video.ended)) {
                this.onVideoEnded();
            }
        },
        onVideoEnded() {
            if (this.videoEndedTriggered) return;
            this.videoEndedTriggered = true;

            if (this.videoSafetyTimeout) {
                clearTimeout(this.videoSafetyTimeout);
            }

            // Safe fallback to ensure the Lottie is rendered if video playback ended early or failed
            this.logoRendered = true;

            const attemptPlayLottie = () => {
                if (this.$refs.logo) {
                    this.videoFinished = true; 
                    this.$refs.logo.playAnimation(1);
                    this.$emit('videoEnded'); // Inform App.vue to start staggered background chunk warmup
                    requestAnimationFrame(this.checkFrame);

                    // 🛡️ Fail-safe fallback: unlock if Lottie freezes in background thread
                    setTimeout(() => {
                        if (!this.triggered) {
                            console.warn("Intro safety timeout: forcing preMount trigger.");
                            this.triggered = true;
                            this.$emit('preMount');
                        }
                    }, 2500);
                } else {
                    setTimeout(attemptPlayLottie, 100);
                }
            };

            attemptPlayLottie();
        },
        checkFrame() {
            if(this.$refs.logo) {
                const currentFrame = this.$refs.logo.getCurrentFrame(); 
                // console.log('testing', currentFrame);

                if (currentFrame >= 140 && !this.triggered) {
                    this.triggered = true;
                    gsap.to(this.$refs.whiteOverlay, {
                        backgroundColor: '#d4d1c8',
                        duration: 0,
                    });
                    this.$emit('preMount'); // ✨ Early DOM-injection trigger: starts DOM mounting 660ms before lock unlocks
                }

                if (!this.triggered) requestAnimationFrame(this.checkFrame);
            }
        },
        triggerAnimationStage2() {
            this.enableInteraction();

            // Detect if deep-linking to a section past intro
            const path = this.$route?.path || window.location.pathname;
            const slug = path.replace(/^\/|\/$/g, '');
            const segments = slug.split('/');
            const activeSection = segments[segments.length - 1] || 'intro';

            const targetSlugs = [
                'circles', 'unlikely-allies', 'developed-by', 'trailer',
                'cinematic', 'cinematic-narrative', 'tactical', 'combat', 'allies',
                'environments', 'faq', 'media', 'videos', 'screenshots', 'downloads'
            ];
            const isDeepLinked = targetSlugs.includes(activeSection.toLowerCase());

            if (isDeepLinked) {
                // Deep-linked past the intro: unlock the page immediately, but
                // DON'T return — we still prepare the scroll indicator below so
                // "Scroll to Enter" appears when the user scrolls back to the top.
                // (The scroll-position check below already keeps it hidden until
                // then, since we're not at the top on a deep link.)
                this.$emit('unlockPage');
            }

            this.animationStage2Finished = true;

            let attempts = 0;
            const attemptPlayScroll = () => {
                attempts++;
                if (this.$refs.scroll) {
                    this.$refs.scroll.playAnimation(1);
                    
                    let lastFrame = -1;
                    this.$refs.scroll.lottie.addEventListener('enterFrame', (e) => {
                        const currentFrame = e.currentTime;
                        if (lastFrame < 22 && currentFrame >= 22) {
                            this.pulseScrollText();
                        }
                        lastFrame = currentFrame;
                    });
                } else if (attempts < 30) {
                    // Safely wait for asynchronous chunk load
                    setTimeout(attemptPlayScroll, 100);
                } else {
                    console.warn("Scroll Indicator Lottie failed to resolve within 3s, unlocking page anyway.");
                }
            };

            this.$nextTick(() => {
                // Only animate the scroll indicator in if the user is still at the top of the page
                if (window.scrollY <= 10) {
                    if (this.$refs.scrollHolder) {
                        this.fadeInTween = markRaw(gsap.fromTo(this.$refs.scrollHolder, 
                            { opacity: 0, scale: 0.8 }, 
                            { opacity: 1, scale: 1, duration: 1.2, ease: "power2.inOut" }
                        ));
                    }
                } else {
                    // Otherwise, set it to opacity 0 instantly (the timeline's ScrollTrigger will smoothly handle scroll up/down)
                    if (this.$refs.scrollHolder) {
                        gsap.set(this.$refs.scrollHolder, { opacity: 0 });
                    }
                }
                attemptPlayScroll();
            });

            // Set up scroll listener to instantly kill the standalone fade-in tween when scrolling starts
            this.scrollCleanHandler = () => {
                if (window.scrollY > 0) {
                    if (this.fadeInTween) {
                        this.fadeInTween.kill();
                        this.fadeInTween = null;
                    }
                    if (this.$refs.scrollHolder) {
                        gsap.set(this.$refs.scrollHolder, { scale: 1 });
                    }
                    window.removeEventListener('scroll', this.scrollCleanHandler);
                }
            };
            window.addEventListener('scroll', this.scrollCleanHandler, { passive: true });

            // Deep links already emitted unlockPage above; avoid emitting twice.
            if (!isDeepLinked) {
                this.$emit('unlockPage');
            }
        },
        pulseScrollText() {
            if (!this.$refs.scrollText) return;
            
            gsap.to(this.$refs.scrollText, {
                letterSpacing: '0.15em',
                duration: 0.4,
                yoyo: true,
                repeat: 1,
                ease: "power2.inOut",
                overwrite: "auto"
            });
        }
    }
};
</script>


